const CAMEL_TO_TITLE_REGEX = /([A-Z])/g;
const FIRST_CHAR_REGEX = /^./;

/**
 * キャメルケースの文字列をタイトルケースに変換します。
 * @param {string} camel - キャメルケースの文字列
 * @returns {string} タイトルケースに変換された文字列
 * @example camelToTitleCase("someColumnId") // Some Column Id
 */
export function camelToTitleCase(camel: string): string {
  return camel.replace(CAMEL_TO_TITLE_REGEX, ' $1').replace(FIRST_CHAR_REGEX, str => str.toUpperCase());
}

/**
 * キャメルケースの文字列をスネークケースに変換します。
 * @param {string} camel - キャメルケースの文字列
 * @returns {string} スネークケースに変換された文字列
 * @example camelToSnake("someColumnId") // some_column_id
 */
export function camelToSnake(camel: string): string {
  return camel.replace(/([A-Z])/g, '_$1').toLowerCase();
}

/**
 * キャメルケースの文字列をアッパースネークケースに変換します。
 * @template T - 文字列の型
 * @param camel - キャメルケースの文字列
 * @returns アッパースネークケースに変換された文字列
 * @example camelToUpperSnake("someColumnId") // SOME_COLUMN_ID
 */
export function camelToUpperSnake<T extends string>(camel: T): Uppercase<T> {
  type Mapped = Uppercase<T>;
  return camel.replace(/([A-Z])/g, '_$1').toUpperCase() as Mapped;
}

/**
 * スネークケースの文字列をキャメルケースに変換します。
 * @param {string} snake - スネークケースの文字列
 * @returns {string} キャメルケースに変換された文字列
 * @example snakeToCamel("some_column_id") // someColumnId
 */
export function snakeToCamel(snake: string): string {
  return snake.replace(/(_\w)/g, match => match[1].toUpperCase());
}

/**
 * スネークケースの文字列をタイトルケースに変換します。
 * @param {string} snake - スネークケースの文字列
 * @returns {string} タイトルケースに変換された文字列
 * @example snakeToTitleCase("some_column_id") // Some Column Id
 */
export function snakeToTitleCase(snake: string): string {
  const camelCase = snakeToCamel(snake);
  return camelToTitleCase(camelCase);
}

/**
 * 全角数字を半角数字に変換します。
 * @param {string} str - 変換する文字列
 * @returns {string} 半角数字に変換された文字列
 * @example convertFullWidthToHalfWidth("１２３４５") // "12345"
 */
export function convertFullWidthToHalfWidth(str: string): string {
  return str.replace(/[！-～]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xfee0));
}

/**
 * 文字列を大文字に変換します。
 * @template T - 文字列の型
 * @param {T} str - 変換する文字列
 * @returns {Uppercase<T>} - 大文字に変換された文字列
 * @example toUpperCase("hello") // "HELLO"
 * NOTE: String.prototype.toUpperCase() は string 型のメソッドなので、String mapped type型定義を使用して型を変換しています。
 */
export function toUpperCase<T extends string>(str: T): Uppercase<T> {
  type Mapped = Uppercase<T>;
  return str.toUpperCase() as Mapped;
}

/**
 * 文字列を小文字に変換します。
 * @template T - 文字列の型
 * @param str - 変換する文字列
 * @returns 小文字に変換された文字列
 * @example toLowerCase("HELLO") // "hello"
 * NOTE: String.prototype.toLowerCase() は string 型のメソッドなので、String mapped type型定義を使用して型を変換しています。
 */
export function toLowerCase<T extends string>(str: T): Lowercase<T> {
  type Mapped = Lowercase<T>;
  return str.toLowerCase() as Mapped;
}

/**
 * 文字列の先頭文字を大文字に変換します。
 * @template T - 文字列の型
 * @param str - 変換する文字列
 * @returns 先頭文字が大文字に変換された文字列
 * @example capitalize("hello") // "Hello"
 */
export function capitalize<T extends string>(str: T): Capitalize<T> {
  type Mapped = Capitalize<T>;
  return str.charAt(0).toUpperCase() + str.slice(1) as Mapped;
}

/**
 * 文字列の先頭文字を小文字に変換します。
 * @template T - 文字列の型
 * @param str - 変換する文字列
 * @returns 先頭文字が小文字に変換された文字列
 * @example uncapitalize("Hello") // "hello"
 */
export function unCapitalize<T extends string>(str: T): Uncapitalize<T> {
  type Mapped = Uncapitalize<T>;
  return str.charAt(0).toLowerCase() + str.slice(1) as Mapped;
}

/**
 * 指定された値が非空の文字列であるかどうかを判定します。
 *
 * @param value - 判定する値
 * @returns 値が非空の文字列である場合はtrue、それ以外の場合はfalse
 */
export const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value !== '';